import React, { createContext, useCallback, useContext, useState } from "react";
import ToastMessage from "@components/ToastMessage/ToastMessage";
import type { ToastMessageProps } from "@components/ToastMessage/ToastMessage.types";

type Notification = Omit<ToastMessageProps, "open" | "onClose"> & { id: string };

interface NotificationContextValue {
  notify: (toast: Omit<Notification, "id">) => void;
}

export const NotificationContext = createContext<NotificationContextValue | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Notification[]>([]);

  const notify = useCallback((toast: Omit<Notification, "id">) => {
    const id = Date.now().toString() + Math.floor(100 + Math.random() * 900).toString();
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}

      {/* Render all active toasts */}
      <div className="fixed top-6 inset-x-0 flex flex-col items-center gap-2 z-1501">
        {toasts.map(({ id, title, description, type, autoHideDuration }) => (
          <ToastMessage
            key={id}
            open
            title={title}
            description={description}
            type={type}
            autoHideDuration={autoHideDuration}
            onClose={() => remove(id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export function useNotification(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be inside NotificationProvider");
  return ctx;
}
